/**
 * schedule — Cron-Ausdrücke lesbar machen und nach vorn rechnen.
 *
 * Ein Autorun trägt seinen Auslöser nicht als Person, sondern als Zeitplan
 * (`0 3 * * *`). Für die Zeitachse braucht die Seite daraus zwei Dinge:
 *
 *   1. **Wann** er das nächste Mal fällig ist — sonst hätte der Eintrag keine
 *      Position auf einer Zeitachse und stünde beziehungslos daneben.
 *   2. **Wer** ihn auslöst, in Worten: „täglich 03:00" statt `0 3 * * *`.
 *
 * Bewusst nur die fünf klassischen Felder (Minute · Stunde · Tag · Monat ·
 * Wochentag) mit `*`, Zahl, Liste, Bereich und Schritt. Alles Exotische
 * (`@daily`, `L`, `#`, Sekundenfeld) ist kein Parse-Fehler mit Knall, sondern
 * fällt still auf „Zeitplan unbekannt" zurück: der Autorun bleibt sichtbar,
 * nur ohne Datum. Eine Seite, die einen Eintrag wegen eines Sonderzeichens
 * verschluckt, wäre die schlechtere Antwort.
 *
 * Zeitzone: gerechnet wird in der lokalen Zone des Browsers, so wie der Runner
 * seine Crons in der lokalen Zone des Servers auswertet. Steht der Browser
 * anderswo, verschiebt sich die Vorschau — deshalb steht am Eintrag immer auch
 * der rohe Ausdruck.
 */

/** Ein geparster Ausdruck: je Feld die erlaubten Werte. */
interface CronSpec {
  minutes: Set<number>;
  hours: Set<number>;
  days: Set<number>;
  months: Set<number>;
  weekdays: Set<number>;
  /** Cron-Regel: sind Tag UND Wochentag eingeschränkt, gilt ODER. */
  dayRestricted: boolean;
  weekdayRestricted: boolean;
}

const WEEKDAY_NAMES = [
  "sonntags",
  "montags",
  "dienstags",
  "mittwochs",
  "donnerstags",
  "freitags",
  "samstags",
];

/** Ein Feld → Menge erlaubter Werte. `null` = nicht verstanden. */
function parseField(spec: string, min: number, max: number): Set<number> | null {
  const out = new Set<number>();
  for (const part of spec.split(",")) {
    const [range, stepRaw] = part.split("/");
    const step = stepRaw === undefined ? 1 : Number(stepRaw);
    if (!Number.isInteger(step) || step < 1) return null;
    let lo: number;
    let hi: number;
    if (range === "*") {
      lo = min;
      hi = max;
    } else if (range.includes("-")) {
      const [a, b] = range.split("-");
      lo = Number(a);
      hi = Number(b);
    } else {
      lo = Number(range);
      hi = stepRaw === undefined ? lo : max;
    }
    if (!Number.isInteger(lo) || !Number.isInteger(hi) || lo < min || hi > max || lo > hi) {
      return null;
    }
    for (let v = lo; v <= hi; v += step) out.add(v);
  }
  return out.size > 0 ? out : null;
}

/** `0 3 * * *` → Spezifikation. `null`, wenn der Ausdruck nicht verstanden wird. */
export function parseCron(expr: string): CronSpec | null {
  const fields = expr.trim().split(/\s+/);
  if (fields.length !== 5) return null;
  const minutes = parseField(fields[0], 0, 59);
  const hours = parseField(fields[1], 0, 23);
  const days = parseField(fields[2], 1, 31);
  const months = parseField(fields[3], 1, 12);
  const rawWeekdays = parseField(fields[4], 0, 7);
  if (!minutes || !hours || !days || !months || !rawWeekdays) return null;
  // Sonntag darf 0 oder 7 heißen.
  const weekdays = new Set<number>(Array.from(rawWeekdays, (d) => (d === 7 ? 0 : d)));
  return {
    minutes,
    hours,
    days,
    months,
    weekdays,
    dayRestricted: fields[2] !== "*",
    weekdayRestricted: fields[4] !== "*",
  };
}

function dayMatches(spec: CronSpec, d: Date): boolean {
  const dom = spec.days.has(d.getDate());
  const dow = spec.weekdays.has(d.getDay());
  if (spec.dayRestricted && spec.weekdayRestricted) return dom || dow;
  if (spec.dayRestricted) return dom;
  if (spec.weekdayRestricted) return dow;
  return true;
}

/** Fensterbreite der Vorschau: ein Jahr. Was danach käme, ist keine Vorschau mehr. */
const HORIZON_DAYS = 366;

/**
 * Die nächste Fälligkeit nach `from` (exklusiv), oder `null` — bei unlesbarem
 * Ausdruck ebenso wie bei einem, der im nächsten Jahr nie feuert (`0 0 30 2 *`).
 *
 * Gesucht wird minutenweise, aber mit Sprüngen: passt der Monat nicht, geht es
 * zum Monatsersten weiter, passt der Tag nicht, zum nächsten Mitternacht. Ein
 * stumpfer Minutenzähler über ein Jahr wären eine halbe Million Schritte je
 * Autorun und je Render.
 */
export function nextOccurrence(expr: string, from: Date | number = Date.now()): Date | null {
  const spec = parseCron(expr);
  if (!spec) return null;
  const start = typeof from === "number" ? new Date(from) : new Date(from.getTime());
  const limit = start.getTime() + HORIZON_DAYS * 24 * 60 * 60 * 1000;

  const d = new Date(start.getTime());
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + 1);

  while (d.getTime() <= limit) {
    if (!spec.months.has(d.getMonth() + 1)) {
      d.setDate(1);
      d.setHours(0, 0, 0, 0);
      d.setMonth(d.getMonth() + 1);
      continue;
    }
    if (!dayMatches(spec, d)) {
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + 1);
      continue;
    }
    if (!spec.hours.has(d.getHours())) {
      d.setMinutes(0, 0, 0);
      d.setHours(d.getHours() + 1);
      continue;
    }
    if (!spec.minutes.has(d.getMinutes())) {
      d.setMinutes(d.getMinutes() + 1);
      continue;
    }
    return d;
  }
  return null;
}

function two(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Der Zeitplan in Worten — das ist die Antwort auf „wer hat das ausgelöst"
 * für einen Autorun. Die häufigen Muster bekommen einen Satz, alles andere
 * bleibt der rohe Ausdruck (ehrlicher als eine erfundene Umschreibung).
 */
export function describeSchedule(expr: string): string {
  const spec = parseCron(expr);
  if (!spec) return expr.trim();
  const fields = expr.trim().split(/\s+/);
  const [minRaw, hourRaw, domRaw, monRaw, dowRaw] = fields;
  const oneMin = spec.minutes.size === 1 ? [...spec.minutes][0] : null;
  const oneHour = spec.hours.size === 1 ? [...spec.hours][0] : null;
  const everyDay = domRaw === "*" && monRaw === "*" && dowRaw === "*";

  if (everyDay && oneMin != null && oneHour != null) {
    return `täglich ${two(oneHour)}:${two(oneMin)}`;
  }
  if (everyDay && oneMin != null && hourRaw === "*") {
    return `stündlich zur Minute ${oneMin}`;
  }
  if (everyDay && hourRaw === "*" && /^\*\/\d+$/.test(minRaw)) {
    return `alle ${minRaw.slice(2)} Minuten`;
  }
  if (
    domRaw === "*" &&
    monRaw === "*" &&
    spec.weekdayRestricted &&
    spec.weekdays.size === 1 &&
    oneMin != null &&
    oneHour != null
  ) {
    return `${WEEKDAY_NAMES[[...spec.weekdays][0]]} ${two(oneHour)}:${two(oneMin)}`;
  }
  if (monRaw === "*" && dowRaw === "*" && spec.days.size === 1 && oneMin != null && oneHour != null) {
    return `monatlich am ${[...spec.days][0]}. um ${two(oneHour)}:${two(oneMin)}`;
  }
  return expr.trim();
}
